import { useWhatsappTemplates } from '@/features/campaigns/v2/hooks/useWhatsappTemplates';
import { useWhatsappChannelConnector, WHATSAPP_CONNECTOR_NAMES } from '@/features/connectors/whatsapp';
import { isWhatsappNumberNotConfiguredError } from '@/features/campaigns/v2/components/WhatsappBusinessOnboardingBlock';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useCompanyValues } from '@/providers';
import { useGetWorkspaceConnectorsQuery } from '@/services/endpoints/workspace-connectors';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SYSTEM_MODELS } from '../../templates/catalog';
import { deriveWaModelStatus, SystemModelStatus } from '../../templates/modelStatus';

export type ModelliStat = { label: string; value: number | null };

export const useModelliWaStatus = () => {
  const { t } = useTranslation('comuni');
  const workspace = useWorkspace();
  const { currentLanguage } = useCompanyValues();
  const { data: customConnectors, isLoading: isConnectorsLoading } = useGetWorkspaceConnectorsQuery(
    { workspaceId: workspace.id, lang: currentLanguage },
    { skip: !workspace.id }
  );

  const isWaAuthenticated = customConnectors?.some((connector) => WHATSAPP_CONNECTOR_NAMES.includes(connector.name) && connector.isAuthenticated) ?? false;

  const {
    templates: waTemplates,
    isLoading: isWaTemplatesLoading,
    isError: isWaTemplatesError,
    error: waTemplatesError,
    reload: reloadWaTemplates,
  } = useWhatsappTemplates({ skip: !isWaAuthenticated });

  // The channel is connected through Whatsapp Business and no number is attached yet. Not a load
  // failure: there is an action to offer, so it gets its own state instead of the error banner.
  const needsWaOnboarding = isWhatsappNumberNotConfiguredError(waTemplatesError);
  const { onboarding } = useWhatsappChannelConnector();

  const connectorsSettled = !isConnectorsLoading;
  const waQuerySettled = !isWaAuthenticated || !isWaTemplatesLoading;
  const isWaZoneReady = connectorsSettled && waQuerySettled && !(isWaAuthenticated && isWaTemplatesError);
  const isWaZoneSettling = isConnectorsLoading || (isWaAuthenticated && isWaTemplatesLoading);
  const showWaZoneError = Boolean(isWaAuthenticated && isWaTemplatesError && !needsWaOnboarding);

  const statusById = useMemo(() => {
    const map = new Map<string, SystemModelStatus>();
    if (!isWaZoneReady) return map;

    for (const model of SYSTEM_MODELS) {
      if (model.channel !== 'whatsapp') continue;
      if (!isWaAuthenticated) {
        map.set(model.id, 'nonInviato');
        continue;
      }
      map.set(model.id, deriveWaModelStatus(model.formData.template_name, waTemplates));
    }
    return map;
  }, [isWaAuthenticated, isWaZoneReady, waTemplates]);

  const stats = useMemo<ModelliStat[]>(() => {
    const total: ModelliStat = { label: t('modelli.stats.totali'), value: SYSTEM_MODELS.length };
    if (isWaZoneSettling) {
      return [
        total,
        { label: t('modelli.stats.approvati'), value: null },
        { label: t('modelli.stats.inRevisione'), value: null },
        { label: t('modelli.stats.nonInviati'), value: null },
      ];
    }

    const statuses = [...statusById.values()];
    return [
      total,
      { label: t('modelli.stats.approvati'), value: statuses.filter((s) => s === 'approvato').length },
      { label: t('modelli.stats.inRevisione'), value: statuses.filter((s) => s === 'inRevisione').length },
      { label: t('modelli.stats.nonInviati'), value: statuses.filter((s) => s === 'nonInviato').length },
    ];
  }, [isWaZoneSettling, statusById, t]);

  return {
    isConnectorsLoading,
    isWaAuthenticated,
    isWaTemplatesLoading,
    isWaTemplatesError: showWaZoneError,
    needsWaOnboarding,
    waOnboardingUrl: onboarding?.onboardingUrl,
    isWaZoneReady,
    isWaZoneSettling,
    reloadWaTemplates,
    statusById,
    stats,
    waTemplates: isWaAuthenticated && !isWaTemplatesError ? waTemplates : [],
  };
};
